"use client";

import React, { useState, useMemo, useEffect } from 'react';
import {
  Search,
  Filter,
  Plus,
  ChevronDown,
  MessageSquare,
  Star,
  ThumbsUp,
  ThumbsDown,
  MoreVertical,
  Edit3,
  Trash2,
  Reply
} from 'lucide-react';
import ReviewModal from './ReviewModal';
import { useReview } from '../context/ReviewContext';
import Pagination, { usePagination } from '../shared/Pagination';
import { CDN_URL } from '../config/apiconfig';

const ReviewsPage = () => {
  // pull data & actions from context
  const { reviews = [], products = [], loading, deleteReview, toggleReviewStatus, refetch } = useReview();

  // local UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Derived options
 const statusOptions = useMemo(() => [
  { value: 'all', label: 'All Reviews', count: reviews.length },
  { value: 'Active', label: 'Active', count: reviews.filter(r => r.status === 'Active').length },
  { value: 'Inactive', label: 'Inactive', count: reviews.filter(r => r.status === 'Inactive').length }
], [reviews]);

  const ratingOptions = [
    { value: 'all', label: 'All Ratings' },
    { value: '5', label: '5 Stars' },
    { value: '4', label: '4 Stars' },
    { value: '3', label: '3 Stars' },
    { value: '2', label: '2 Stars' },
    { value: '1', label: '1 Star' }
  ];

const productOptions = useMemo(() => [
  { value: 'all', label: 'All Products' },
  ...products
    .filter(product => product.status === 'Active')  // ← add this line
    .map(product => ({
      value: product.id,
      label: product.product_name.length > 30
        ? product.product_name.substring(0, 30) + '...'
        : product.product_name
    }))
], [products]);

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'rating-high', label: 'Highest Rating' },
    { value: 'rating-low', label: 'Lowest Rating' },
    { value: 'helpful', label: 'Most Helpful' }
  ];

  // Filter + Sort logic (memoized)
const filteredAndSortedReviews = useMemo(() => {
  let filtered = reviews.filter(review => {
    const matchesSearch =
      searchTerm === '' ||
      (review.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (review.description || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' || review.status === statusFilter;

    const matchesRating =
      ratingFilter === 'all' ||
      String(review.rating) === String(ratingFilter);

    const matchesProduct =
      productFilter === 'all' ||
      String(review.product_id) === String(productFilter);

    return matchesSearch && matchesStatus && matchesRating && matchesProduct;
  });

  filtered.sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.create_audit_time) - new Date(a.create_audit_time);
      case 'oldest':
        return new Date(a.create_audit_time) - new Date(b.create_audit_time);
      case 'rating-high':
        return (b.rating || 0) - (a.rating || 0);
      case 'rating-low':
        return (a.rating || 0) - (b.rating || 0);
      default:
        return 0;
    }
  });

  return filtered;
}, [reviews, searchTerm, statusFilter, ratingFilter, productFilter, sortBy]);

const stats = useMemo(() => {
  const totalReviews = reviews.length;

  const averageRating =
    totalReviews === 0
      ? 0
      : reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / totalReviews;

  const activeReviews = reviews.filter(r => r.status === 'Active').length;
  const inactiveReviews = reviews.filter(r => r.status === 'Inactive').length;
  const flaggedReviews = reviews.filter(r => r.status === 'Flagged').length;

  return {
    totalReviews,
    averageRating: averageRating.toFixed(1),
    activeReviews,
    inactiveReviews,
    flaggedReviews
  };
}, [reviews]);

  const { page: rPage, pageSize: rSize, setPage: setRPage, setPageSize: setRSize, totalPages: rTotalPages, totalItems: rTotal, paginated: paginatedReviews } = usePagination(filteredAndSortedReviews, 10);

  // selection handlers
  const handleSelectReview = (reviewId) => {
    setSelectedReviews(prev => prev.includes(reviewId) ? prev.filter(id => id !== reviewId) : [...prev, reviewId]);
  };

  const handleSelectAll = () => {
    if (selectedReviews.length === filteredAndSortedReviews.length) {
      setSelectedReviews([]);
    } else {
      setSelectedReviews(filteredAndSortedReviews.map(r => r.id));
    }
  };

  // CRUD actions now call context functions
  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;

    try {
      await deleteReview(reviewId);
      setSelectedReviews(prev => prev.filter(id => id !== reviewId));
    } catch (err) {
      console.error(err);
      alert('Failed to delete review');
    } finally {
      setActiveDropdown(null);
    }
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
    setShowAddModal(true);
    setActiveDropdown(null);
  };

  const handleUpdateStatus = async (reviewId, newStatus) => {
    try {
      await toggleReviewStatus(reviewId, newStatus); // context expects (id, currentStatus) in our earlier impl; adapt if needed
    } catch (err) {
      console.error(err);
      alert('Failed to update status');
    } finally {
      setActiveDropdown(null);
    }
  };

  const handleSaveReview = (reviewData) => {
    // Ideally ReviewModal should call context.addReview or update via API.
    // For now, after save close modal and refetch
    setShowAddModal(false);
    setEditingReview(null);
    refetch?.();
  };

  const handleAddReview = () => {
    setEditingReview(null);
    setShowAddModal(true);
  };

  const renderStars = (rating) => (
    Array.from({ length: 5 }, (_, index) => (
      <Star key={index} className={`w-4 h-4 ${index < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
    ))
  );

  // quick guard while loading
  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto text-center py-20">Loading reviews...</div>
      </div>
    );
  }

  const ReviewItem = ({ review }) => {
    const isSelected = selectedReviews.includes(review.id);

    return (
      <div className={`bg-white/80 rounded-xl border transition-all duration-300 ${isSelected ? 'border-teal-400 shadow-lg' : 'border-gray-200 hover:shadow-lg hover:shadow-gray-200/50'}`}>
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-4 flex-1">
           

              <div className="flex items-start gap-4 flex-1">
                <img
                  src={review.user_image ? `${CDN_URL}/${review.user_image}` : "/default-avatar.png"}
                  alt={review.customerName}
                  className="w-12 h-12 rounded-full border-2 border-gray-200 flex-shrink-0"
                />

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-lg font-semibold text-teal-800 mb-1">{review.user_name} {review.last_name}</h4>
                      <p className="text-sm text-gray-600 mb-2">{review.productName}</p>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center gap-1">{renderStars(review.rating)}</div>
                        <span className="text-sm text-gray-600">({review.rating}/5)</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${review.status === 'Active' ? 'bg-green-100 text-green-800' : review.status === 'Inactive' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>{review.status}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    {/* <h3 className="font-semibold text-gray-800 mb-2 text-left">{review.description}</h3> */}
                    <p className="text-gray-700 leading-relaxed text-left">{review.description}</p>
                  </div>

                  {review.adminResponse && (
                    <div className="bg-teal-50 border-l-4 border-teal-400 p-4 rounded mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Reply className="w-4 h-4 text-teal-600" />
                        <span className="text-sm font-medium text-teal-800">Admin Response</span>
                      </div>
                      <p className="text-sm text-teal-700 text-left">{review.adminResponse}</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-6">
                      {/* <div className="flex items-center gap-1"><ThumbsUp className="w-4 h-4" /> <span>{review.helpful || 0} helpful</span></div>
                      <div className="flex items-center gap-1"><ThumbsDown className="w-4 h-4" /> <span>{review.notHelpful || 0} not helpful</span></div> */}
                    </div>
                    <span>Posted: {review.create_audit_time ? new Date(review.create_audit_time).toLocaleDateString() : '—'}</span>
                  </div>

                <div className="flex gap-2 pt-3 border-t border-gray-100">

  {/* EDIT BUTTON */}
  <button
    onClick={() => handleEditReview(review)}
    className="flex items-center justify-center p-2 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors"
  >
    <Edit3 className="w-4 h-4" />
  </button>

  {/* STATUS BUTTON */}
  <button
    onClick={() => handleUpdateStatus(review.id, review.status)}
    className={`flex items-center justify-center p-2 rounded-lg transition-colors
      ${review.status === "Active"
        ? "bg-green-50 text-green-700 hover:bg-green-100"
        : "bg-gray-50 text-orange-700 hover:bg-gray-100"}
    `}
  >
    {review.status === "Active" ? (
      <ThumbsUp className="w-4 h-4" />
    ) : (
      <ThumbsDown className="w-4 h-4" />
    )}
  </button>

  {/* DELETE BUTTON */}
  <button
    onClick={() => handleDeleteReview(review.id)}
    className="flex items-center justify-center p-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
  >
    <Trash2 className="w-4 h-4" />
  </button>

</div>
                </div>
              </div>
            </div>

          
          </div>
        </div>
      </div>
    );
  };

  const CustomSelect = ({ value, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  const selected = options.find(o => String(o.value) === String(value));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white text-left flex justify-between items-center text-sm"
      >
        <span className="truncate">
          {selected?.label?.length > 30
            ? selected.label.substring(0, 30) + '...'
            : selected?.label}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <ul className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200  shadow-lg z-[999] max-h-48 overflow-y-auto">
          {options.map(option => (
            <li
              key={option.value}
              onClick={() => { onChange(String(option.value)); setOpen(false); }}
              className={`px-3 py-2 text-sm cursor-pointer truncate transition-colors
  ${
    String(value) === String(option.value)
      ? 'bg-blue-600 text-white font-medium'
      : 'text-gray-700 hover:bg-blue-50 hover:text-blue-700'
  }
`}
              title={option.label}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label:'Total Reviews',    value:stats.totalReviews,    icon:MessageSquare, accent:'border-l-teal-500',   color:'text-teal-700',   bg:'bg-teal-50' },
            { label:'Avg Rating',       value:stats.averageRating,   icon:Star,          accent:'border-l-yellow-400', color:'text-yellow-600', bg:'bg-yellow-50' },
            { label:'Active Reviews',   value:stats.activeReviews,   icon:ThumbsUp,      accent:'border-l-green-500',  color:'text-green-600',  bg:'bg-green-50' },
            { label:'Inactive Reviews', value:stats.inactiveReviews, icon:ThumbsDown,    accent:'border-l-red-400',    color:'text-red-500',    bg:'bg-red-50' },
          ].map(({ label, value, icon:Icon, accent, color, bg }) => (
            <div key={label} className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-4 flex items-center gap-3 shadow-sm`}>
              <div className={`w-9 h-9 ${bg} rounded-lg flex items-center justify-center flex-shrink-0`}><Icon className={`w-4 h-4 ${color}`} /></div>
              <div><p className={`text-xl font-bold ${color}`}>{value}</p><p className="text-xs text-gray-400 leading-tight">{label}</p></div>
            </div>
          ))}
        </div>

        {/* Search and Filters */}
        <section className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
         <div className="flex flex-wrap gap-3 items-center mb-0">
           <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search reviews by customer, product, or content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:bg-white transition"
            />
           </div>
          <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 text-teal-600 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white hover:text-teal-800 transition-colors"><Filter className="w-4 h-4" /><span>Filters</span><ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} /></button>
          <button onClick={handleAddReview} className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-xl font-semibold text-sm transition-colors shadow-sm flex-shrink-0"><Plus className="w-4 h-4" /><span className="hidden sm:inline">Add Review</span></button>
</div>
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400">
                  {statusOptions.map(option => (<option key={option.value} value={option.value}>{option.label} ({option.count})</option>))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                <select value={ratingFilter} onChange={(e) => setRatingFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400">
                  {ratingOptions.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product</label>
               <CustomSelect
  value={productFilter}
  onChange={setProductFilter}
  options={productOptions}
/>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400">
                  {sortOptions.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
                </select>
              </div>

              {selectedReviews.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bulk Actions ({selectedReviews.length} selected)</label>
                  <div className="flex gap-2">
                    <button onClick={async () => {
                      // Approve all selected
                      for (const id of selectedReviews) await toggleReviewStatus(
  id,
  reviews.find(r => r.id === id)?.status
);
                      setSelectedReviews([]);
                    }} className="px-3 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm">Approve All</button>
                    <button onClick={async () => {
                      if (!window.confirm('Are you sure you want to delete this review?')) return;

                      for (const id of selectedReviews) await deleteReview(id);
                      setSelectedReviews([]);
                    }} className="px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm">Delete Selected</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Reviews List */}
        <section>
          <div className="flex items-center justify-between mb-6">
           
            <span className="text-sm text-gray-600">Showing {filteredAndSortedReviews.length} of {reviews.length} reviews</span>
          </div>

       {filteredAndSortedReviews.length === 0 ? (
  <div className="bg-white border border-gray-200 rounded-2xl py-20 text-center shadow-sm">
    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
      <MessageSquare className="w-8 h-8 text-gray-300" />
    </div>
    <h3 className="text-lg font-semibold text-gray-700 mb-1">No reviews found</h3>
    <p className="text-sm text-gray-400 mb-5">
      {searchTerm || statusFilter !== 'all' || ratingFilter !== 'all' || productFilter !== 'all'
        ? 'Try adjusting your search or filters.'
        : 'No customer reviews yet.'}
    </p>
    {searchTerm || statusFilter !== 'all' || ratingFilter !== 'all' || productFilter !== 'all' ? (
      <button
        onClick={() => {
          setSearchTerm('');
          setStatusFilter('all');
          setRatingFilter('all');
          setProductFilter('all');
        }}
        className="px-5 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
      >
        Clear filters
      </button>
    ) : (
      <button
        onClick={handleAddReview}
        className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-sm font-semibold transition-colors"
      >
        Add Review
      </button>
    )}
  </div>
) : (
            <>
              <div className="space-y-4">
                {paginatedReviews.map((review) => (<ReviewItem key={review.id} review={review} />))}
              </div>
              <div className="bg-white border border-gray-200 rounded-xl px-4 mt-3">
                <Pagination currentPage={rPage} totalPages={rTotalPages} totalItems={rTotal}
                  pageSize={rSize} onPageChange={setRPage} onPageSizeChange={setRSize} />
              </div>
            </>
          )}
        </section>
      </div>

      {/* Review Modal */}
      <ReviewModal isOpen={showAddModal} onClose={() => { setShowAddModal(false); setEditingReview(null); }} review={editingReview} products={products} onSave={handleSaveReview} />

      {/* Click outside handler for dropdowns */}
      {activeDropdown && (<div className="fixed inset-0 z-40" onClick={() => setActiveDropdown(null)} />)}
    </div>
  );
};

export default ReviewsPage;
